import React, { useState } from 'react';

import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Tooltip } from '../Tooltip/Tooltip';

import {
  InteractionMode,
  IInteractionModeOption,
  IInteractionModeSelectorProps,
} from './InteractionModeSelector.type';

const ALL_OPTIONS: IInteractionModeOption[] = [
  { value: InteractionMode.AGENT, label: 'Build', description: 'Create, edit & ship changes to your codebase', icon: IconName.Code },
  { value: InteractionMode.ASK, label: 'Ask', description: 'Ask anything about your codebase (No changes made)', icon: IconName.Read },
];

export const InteractionModeSelector: React.FC<IInteractionModeSelectorProps> = ({
  mode,
  onChange,
  disabled = false,
  lockedToAsk = false,
}) => {
  const [open, setOpen] = useState(false);

  const options = lockedToAsk
    ? ALL_OPTIONS.filter(o => o.value === InteractionMode.ASK)
    : ALL_OPTIONS;
  const selected = options.find(o => o.value === mode) ?? options[0];

  const dropdownContent = (onClose: () => void) => (
    <div className="bg-bg-primary overflow-hidden" role="listbox" aria-label="Chat mode">
      <div className="flex items-center justify-between px-bg-v2-md py-bg-v2-md">
        <Text variant="text-xs" weight="semibold" className="text-text-quaternary tracking-widest uppercase">
          Chat Mode
        </Text>
      </div>

      <div className="flex-col gap-bg-v2-xxs">
        {options.map((option) => {
          const isSelected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                onChange(option.value);
                onClose();
                setOpen(false);
              }}
              className={`
                w-full text-left rounded-bg-v2-lg px-bg-v2-md py-bg-v2-md
                flex items-center gap-bg-v2-md
                transition-all duration-150 group
                ${isSelected ? 'bg-bg-quaternary' : 'hover:bg-bg-secondary'}
              `}
            >
              <Icon
                name={option.icon}
                size="sm"
                className={isSelected ? 'text-text-primary' : 'text-text-tertiary'}
                inheritColor
              />
              <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                <Text
                  variant="text-sm"
                  weight={isSelected ? 'semibold' : 'medium'}
                  className="text-text-primary"
                >
                  {option.label}
                </Text>
                <Text variant="text-xs" className={`leading-snug ${isSelected ? 'text-text-secondary' : 'text-text-tertiary'}`}>
                  {option.description}
                </Text>
              </span>

              <span className={`flex-shrink-0 transition-opacity duration-150 ${isSelected ? 'opacity-100' : 'opacity-0'}`}>
                <Icon name={IconName.Check} size="xs" className="text-text-primary" inheritColor />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const isDisabled = disabled || lockedToAsk;

  return (
    <Tooltip
      content={dropdownContent}
      position="top"
      interactive
      trigger="contextmenu"
      delay={0}
      onVisibleChange={setOpen}
    >
      <button
        type="button"
        disabled={isDisabled}
        className={`
          flex items-center gap-bg-v2-sm
          px-bg-v2-lg py-bg-v2-md
          rounded-bg-v2-full
          border border-border-secondary w-full
          bg-bg-secondary
          transition-all duration-150
          ${isDisabled
            ? 'opacity-60 cursor-not-allowed'
            : open
              ? 'bg-bg-quaternary border-border-primary text-text-primary'
              : 'hover:bg-bg-primary-hover hover:border-border-primary cursor-pointer'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={lockedToAsk ? 'Read-only access: Ask mode only' : 'Select chat mode'}
      >
        <Icon
          name={selected.icon}
          size="sm"
          className={open ? 'text-text-primary' : 'text-text-secondary'}
          inheritColor
        />
        <Text
          variant="text-sm"
          weight="medium"
          className={`transition-colors duration-150 ${open ? 'text-text-primary' : 'text-text-secondary'}`}
        >
          {selected.label}
        </Text>
        {!lockedToAsk && (
          <Icon
            name={IconName.ChevronDown}
            size="sm"
            className={`transition-all duration-200 ${open ? 'text-text-primary rotate-180' : 'text-text-quaternary'}`}
            inheritColor
          />
        )}
      </button>
    </Tooltip>
  );
};
